import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SFStack, SFTypography, SFUseStyled, SFIcons } from "@sf-core";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { profileActions } from "../../../redux/reducers/profileReducer";
import StepContainer from "../stepContainer";
import {
  saveStep,
  getUserJobRole,
  getUserJobGoals,
  getUserSkillGoals,
} from "../../../services/profileService";
import { useSelector } from "react-redux";

const { styled } = SFUseStyled();

const StyledIcon = styled(SFIcons.SFCheckIcon)(({ theme }) => ({
  color: theme.palette.grey400.main,
}));

const SetupCompletion = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobsOfInterest, setShowJobsOfInterest] = useState(false);
  const [showSkillGoals, setShowSkillGoals] = useState(false);

  const existingJobGoals = useSelector((state) => state.profile.jobGoals);
  const existingSkillGoals = useSelector((state) => state.profile.skillGoals);

  // On step load
  useEffect(() => {
    props.setProgressValue(100);
    props.setDisableNext(true);
    // request to get user job role
    getUserJobRole().then((res) => {
      setSelectedJob(res.data.name);
    });
    // request to show if user has selected jobs of interest
    if (existingJobGoals.length > 0) {
      setShowJobsOfInterest(true);
    } else {
      getUserJobGoals().then((res) => {
        setShowJobsOfInterest(res.data.length > 0);
      });
    }
    // request to show if user has selected skill goals
    if (existingSkillGoals.length > 0) {
      setShowSkillGoals(true);
    } else {
      getUserSkillGoals().then((res) => {
        setShowSkillGoals(res.data.length > 0);
      });
    }
  }, []);

  const onStepClose = async (goStepBack = false, stepCompleted = true) => {
    let data = {
      currentStep: 4,
      isStepCompleted: stepCompleted,
    };
    // Request to save the user's profile completion and then close Profile Stepper
    await saveStep(data);
    props.setDisableNext(false);
    if (goStepBack) props.navigateStepBack();
    else {
      dispatch(
        profileActions.setState({
          saveUserProfileProgress: true,
        })
      );
    }
  };

  useImperativeHandle(ref, () => ({
    onStepClose,
  }));

  return (
    <StepContainer
      stepTitle={t("profileScreen.setupCompletion.title")}
      stepDescription={t("profileScreen.setupCompletion.description")}
      onTransparentButtonClick={() => onStepClose(true)}
      transparentButtonLabel={t("profileScreen.btnBack")}
      onSecondaryButtonClick={onStepClose}
      secondaryButtonLabel={t("profileScreen.setupCompletion.btnFinish")}
      stepCompleted
    >
      <SFStack direction="column" justifyContent="flex-start" spacing={1}>
        {selectedJob && (
          <>
            <SFStack direction="row" spacing={1.5}>
              <StyledIcon />
              <SFTypography variant={"body2"}>
                {t("profileScreen.setupCompletion.jobTitle", {
                  value: selectedJob,
                })}
              </SFTypography>
            </SFStack>
            <SFStack direction="row" spacing={1.5}>
              <StyledIcon />
              <SFTypography variant={"body2"}>
                {t("profileScreen.setupCompletion.skillsLevels")}
              </SFTypography>
            </SFStack>
          </>
        )}
        {showJobsOfInterest && (
          <SFStack direction="row" spacing={1.5}>
            <StyledIcon />
            <SFTypography variant={"body2"}>
              {t("profileScreen.setupCompletion.jobsOfInterest")}
            </SFTypography>
          </SFStack>
        )}
        {showSkillGoals && (
          <SFStack direction="row" spacing={1.5}>
            <StyledIcon />
            <SFTypography variant={"body2"}>
              {t("profileScreen.setupCompletion.skillGoals")}
            </SFTypography>
          </SFStack>
        )}
      </SFStack>
    </StepContainer>
  );
});

export default SetupCompletion;
